import useCurrentSubscriptionHelper from 'hooks/billing/useCurrentSubscriptionHelper';
import React, { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { getPlanLimitsCountThunk, PlanLimitsCountState } from 'redux/slices/plan-limits-counter';
import { Feature } from '../../../pb/subscription/v1/addon_pb';

export interface AddonLimit {
    id: number;
    name: Feature | string;
    value: string;
    currentlyUsed: string;
}

export interface UsePlanReturn {
    refresh: () => void;
    planLimits: PlanLimitsCountState;
    getMonthlyOrderLimit: () => AddonLimit | undefined;
    getMonthlyProviderLimit: () => AddonLimit | undefined;
    isUnlimitedOrderLimit: () => boolean;
    isMonthlyProviderLimitReached: (selectedProviderCount: number) => boolean;
    hasIntercomSupport: boolean;
    loading: boolean;
}

const usePlan = (): UsePlanReturn => {
    const dispatch = useAppDispatch();

    const planLimits = useAppSelector((state) => state.planLimitsCount);
    const { addons, isLoading: isSubscriptionLoading, isLegacy } = useCurrentSubscriptionHelper();

    const refresh = () => {
        dispatch(getPlanLimitsCountThunk());
    };

    const getAddonLimit = (name: Feature) => {
        return planLimits.limits?.find((limit) => limit.name === name);
    };

    const getMonthlyOrderLimit = () => getAddonLimit(Feature.MONTHLY_ORDER_LIMIT);

    const getMonthlyProviderLimit = () => getAddonLimit(Feature.PACKAGE_PROVIDER_LIMIT);

    const isMonthlyProviderLimitReached = (selectedProviderCount: number): boolean => {
        const limit = getMonthlyProviderLimit();
        if (!limit) return false;
        return selectedProviderCount > parseInt(limit.value, 10);
    };

    const isUnlimitedOrderLimit = (): boolean => {
        return parseInt(getMonthlyOrderLimit()?.value, 10) === -1;
    };

    const hasIntercomSupport = useMemo(() => {
        if (!isLegacy) return addons.some((a) => a.name === Feature.SUPPORT_ENABLED);
        return (
            !planLimits.limits ||
            planLimits.limits.length < 1 ||
            planLimits.limits.some((limit) => limit.name === Feature.SUPPORT_ENABLED)
        );
    }, [isLegacy, addons, planLimits.limits]);

    return {
        refresh,
        planLimits,
        getMonthlyOrderLimit,
        getMonthlyProviderLimit,
        isUnlimitedOrderLimit,
        isMonthlyProviderLimitReached,
        hasIntercomSupport,
        loading: planLimits.loading || isSubscriptionLoading,
    };
};

export default usePlan;

export type UsePlanProp = {
    children: React.ReactNode;
};
